import React, { useState, useRef, useMemo } from 'react';
import {
  View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet,
} from 'react-native';
import { RD, fonts } from '../theme/colors';

// ─── Vehicle database organized by vehicle type ───────────────────────────────

type VehicleEntry = { make: string; model: string };

const VEHICLES_BY_TYPE: Record<string, VehicleEntry[]> = {
  VAN: [
    // Ford
    { make: 'Ford', model: 'Transit Cargo Van' },
    { make: 'Ford', model: 'Transit Passenger Van' },
    { make: 'Ford', model: 'Transit Connect' },
    { make: 'Ford', model: 'E-Transit' },
    // Mercedes-Benz
    { make: 'Mercedes-Benz', model: 'Sprinter 1500' },
    { make: 'Mercedes-Benz', model: 'Sprinter 2500' },
    { make: 'Mercedes-Benz', model: 'Sprinter 3500' },
    { make: 'Mercedes-Benz', model: 'Sprinter 3500 XD' },
    { make: 'Mercedes-Benz', model: 'Metris' },
    // RAM
    { make: 'RAM', model: 'ProMaster 1500' },
    { make: 'RAM', model: 'ProMaster 2500' },
    { make: 'RAM', model: 'ProMaster 3500' },
    { make: 'RAM', model: 'ProMaster City' },
    // Chevrolet
    { make: 'Chevrolet', model: 'Express 2500' },
    { make: 'Chevrolet', model: 'Express 3500' },
    // GMC
    { make: 'GMC', model: 'Savana 2500' },
    { make: 'GMC', model: 'Savana 3500' },
    // Nissan
    { make: 'Nissan', model: 'NV200 Compact Cargo' },
    { make: 'Nissan', model: 'NV1500' },
    { make: 'Nissan', model: 'NV2500 HD' },
    { make: 'Nissan', model: 'NV3500 HD' },
    // Toyota
    { make: 'Toyota', model: 'Hiace' },
    // Freightliner
    { make: 'Freightliner', model: 'Sprinter 2500' },
    { make: 'Freightliner', model: 'Sprinter 3500' },
  ],

  PICKUP_TRUCK: [
    // Ford
    { make: 'Ford', model: 'F-150' },
    { make: 'Ford', model: 'F-150 Lightning' },
    { make: 'Ford', model: 'F-250 Super Duty' },
    { make: 'Ford', model: 'F-350 Super Duty' },
    { make: 'Ford', model: 'F-450 Super Duty' },
    { make: 'Ford', model: 'Ranger' },
    { make: 'Ford', model: 'Maverick' },
    // Chevrolet
    { make: 'Chevrolet', model: 'Silverado 1500' },
    { make: 'Chevrolet', model: 'Silverado 2500 HD' },
    { make: 'Chevrolet', model: 'Silverado 3500 HD' },
    { make: 'Chevrolet', model: 'Colorado' },
    // RAM
    { make: 'RAM', model: '1500' },
    { make: 'RAM', model: '2500' },
    { make: 'RAM', model: '3500' },
    // GMC
    { make: 'GMC', model: 'Sierra 1500' },
    { make: 'GMC', model: 'Sierra 2500 HD' },
    { make: 'GMC', model: 'Sierra 3500 HD' },
    { make: 'GMC', model: 'Canyon' },
    // Toyota
    { make: 'Toyota', model: 'Tacoma' },
    { make: 'Toyota', model: 'Tundra' },
    // Nissan
    { make: 'Nissan', model: 'Frontier' },
    { make: 'Nissan', model: 'Titan' },
    { make: 'Nissan', model: 'Titan XD' },
    // Honda
    { make: 'Honda', model: 'Ridgeline' },
    // Hyundai
    { make: 'Hyundai', model: 'Santa Cruz' },
    // Rivian
    { make: 'Rivian', model: 'R1T' },
    // Tesla
    { make: 'Tesla', model: 'Cybertruck' },
  ],

  BOX_TRUCK: [
    // Ford
    { make: 'Ford', model: 'E-350 Cutaway' },
    { make: 'Ford', model: 'E-450 Cutaway' },
    { make: 'Ford', model: 'F-59' },
    { make: 'Ford', model: 'F-650' },
    { make: 'Ford', model: 'F-750' },
    // Isuzu
    { make: 'Isuzu', model: 'NPR' },
    { make: 'Isuzu', model: 'NPR-HD' },
    { make: 'Isuzu', model: 'NPR-XD' },
    { make: 'Isuzu', model: 'NQR' },
    { make: 'Isuzu', model: 'NRR' },
    { make: 'Isuzu', model: 'FTR' },
    { make: 'Isuzu', model: 'FVR' },
    // Freightliner
    { make: 'Freightliner', model: 'M2 106' },
    { make: 'Freightliner', model: 'M2 112' },
    { make: 'Freightliner', model: 'eM2' },
    { make: 'Freightliner', model: 'P700' },
    { make: 'Freightliner', model: 'P900' },
    { make: 'Freightliner', model: 'P1200' },
    // Hino
    { make: 'Hino', model: 'L6' },
    { make: 'Hino', model: 'L7' },
    { make: 'Hino', model: 'XL7' },
    { make: 'Hino', model: 'XL8' },
    { make: 'Hino', model: '155' },
    { make: 'Hino', model: '195' },
    { make: 'Hino', model: '268' },
    { make: 'Hino', model: '338' },
    // Mitsubishi Fuso
    { make: 'Mitsubishi Fuso', model: 'FE140' },
    { make: 'Mitsubishi Fuso', model: 'FE160' },
    { make: 'Mitsubishi Fuso', model: 'FE180' },
    { make: 'Mitsubishi Fuso', model: 'FK65' },
    // International
    { make: 'International', model: 'CV515' },
    { make: 'International', model: 'MV607' },
    { make: 'International', model: 'HV507' },
    // Kenworth
    { make: 'Kenworth', model: 'K270' },
    { make: 'Kenworth', model: 'K370' },
    // Peterbilt
    { make: 'Peterbilt', model: '536' },
    { make: 'Peterbilt', model: '537' },
    // RAM
    { make: 'RAM', model: 'ProMaster 3500 Cutaway' },
    { make: 'RAM', model: 'ProMaster 4500 Cutaway' },
    // Chevrolet
    { make: 'Chevrolet', model: 'Express 3500 Cutaway' },
    { make: 'Chevrolet', model: 'Express 4500 Cutaway' },
    // GMC
    { make: 'GMC', model: 'Savana 3500 Cutaway' },
    { make: 'GMC', model: 'Savana 4500 Cutaway' },
    // Morgan / Supreme (body builders)
    { make: 'Morgan', model: 'City Van Body 12ft' },
    { make: 'Morgan', model: 'City Van Body 16ft' },
    { make: 'Supreme', model: 'Iner-City 12ft' },
    { make: 'Supreme', model: 'Iner-City 16ft' },
    { make: 'Supreme', model: 'Iner-City 20ft' },
    { make: 'Supreme', model: 'Iner-City 26ft' },
  ],

  TRAILER: [
    // Utility Trailer
    { make: 'Utility Trailer', model: '4000D-X' },
    { make: 'Utility Trailer', model: '4000AE' },
    { make: 'Utility Trailer', model: 'VS2DC' },
    { make: 'Utility Trailer', model: 'VS2RA' },
    // Great Dane
    { make: 'Great Dane', model: 'Champion CS1' },
    { make: 'Great Dane', model: 'Champion CL' },
    { make: 'Great Dane', model: 'Freedom SE' },
    { make: 'Great Dane', model: 'Everest' },
    // Wabash
    { make: 'Wabash', model: 'DuraPlate Dryvan' },
    { make: 'Wabash', model: 'DuraPlate HD' },
    { make: 'Wabash', model: 'ArcticLite' },
    // Hyundai Translead
    { make: 'Hyundai Translead', model: 'Dryvan' },
    { make: 'Hyundai Translead', model: 'Reefer' },
    { make: 'Hyundai Translead', model: 'Chassis' },
    // Stoughton
    { make: 'Stoughton', model: 'Z-Plate' },
    { make: 'Stoughton', model: 'Pureplate' },
    // Vanguard
    { make: 'Vanguard', model: 'VXP Dryvan' },
    { make: 'Vanguard', model: 'Cool Globe' },
    // CIMC
    { make: 'CIMC', model: 'Vanguard Dryvan' },
    { make: 'CIMC', model: 'Intermodal Chassis' },
    // PJ Trailers
    { make: 'PJ Trailers', model: 'Gooseneck Flatbed' },
    { make: 'PJ Trailers', model: 'Car Hauler' },
    { make: 'PJ Trailers', model: 'Dump Trailer' },
    // Big Tex
    { make: 'Big Tex', model: '22GN Gooseneck' },
    { make: 'Big Tex', model: '14ET Equipment' },
    { make: 'Big Tex', model: '70CH Car Hauler' },
    // Load Trail
    { make: 'Load Trail', model: 'Gooseneck Flatbed' },
    { make: 'Load Trail', model: 'Enclosed Cargo' },
    // Carry-On
    { make: 'Carry-On', model: 'Enclosed Cargo 6x12' },
    { make: 'Carry-On', model: 'Enclosed Cargo 7x16' },
    { make: 'Carry-On', model: 'Utility 5x8' },
    { make: 'Carry-On', model: 'Utility 6x12' },
  ],
};

const YEARS = Array.from({ length: 30 }, (_, i) => String(2026 - i));

interface Props {
  value: string;
  onChangeText: (text: string) => void;
  vehicleType: string;
}

export function VehicleAutocomplete({ value, onChangeText, vehicleType }: Props) {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showList, setShowList] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const vehicles = useMemo(() => VEHICLES_BY_TYPE[vehicleType] || [], [vehicleType]);

  const generateSuggestions = (text: string): string[] => {
    if (text.length < 1) return [];
    const lower = text.toLowerCase();
    const results: string[] = [];

    const yearMatch = text.match(/^((?:19|20)\d{2})\s*/);
    const afterYear = yearMatch ? text.slice(yearMatch[0].length).toLowerCase().trim() : lower;

    for (const { make, model } of vehicles) {
      const full = `${make} ${model}`.toLowerCase();
      const makeLower = make.toLowerCase();

      if (afterYear.length === 0 || full.includes(afterYear) || makeLower.startsWith(afterYear)) {
        if (yearMatch) {
          results.push(`${yearMatch[1]} ${make} ${model}`);
        } else {
          results.push(`${make} ${model}`);
        }
      }
    }

    return [...new Set(results)].slice(0, 10);
  };

  const handleChange = (text: string) => {
    onChangeText(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const s = generateSuggestions(text);
      setSuggestions(s);
      setShowList(s.length > 0);
    }, 120);
  };

  const handleSelect = (suggestion: string) => {
    onChangeText(suggestion);
    setShowList(false);
    setSuggestions([]);
  };

  const typeLabel = vehicleType.replace(/_/g, ' ').toLowerCase();

  return (
    <View style={styles.container}>
      <TextInput
        style={styles.input}
        placeholder={`Search ${typeLabel} make & model...`}
        value={value}
        onChangeText={handleChange}
        onFocus={() => {
          if (value.length >= 1 && suggestions.length > 0) {
            setShowList(true);
          } else if (value.length < 1) {
            const s = generateSuggestions('');
            if (s.length === 0) {
              // Show all if nothing typed
              const all = vehicles.map(v => `${v.make} ${v.model}`);
              setSuggestions(all.slice(0, 10));
              setShowList(all.length > 0);
            } else {
              setSuggestions(s);
              setShowList(s.length > 0);
            }
          }
        }}
        onBlur={() => setTimeout(() => setShowList(false), 200)}
        placeholderTextColor={RD.textMuted}
        autoComplete="off"
        autoCorrect={false}
      />
      {showList && suggestions.length > 0 && (
        <ScrollView style={styles.listContainer} keyboardShouldPersistTaps="handled" nestedScrollEnabled>
          {suggestions.map((item, idx) => (
            <TouchableOpacity
              key={`${item}-${idx}`}
              style={styles.row}
              onPress={() => handleSelect(item)}
            >
              <Text style={styles.rowText}>{item}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { position: 'relative', zIndex: 999 },
  input: {
    backgroundColor: RD.surface, borderRadius: 12,
    paddingHorizontal: 16, paddingVertical: 14,
    fontSize: 15, borderWidth: 1.5, borderColor: RD.borderStrong,
    color: RD.text, fontFamily: fonts.regular,
  },
  listContainer: {
    position: 'absolute', top: '100%', left: 0, right: 0,
    backgroundColor: RD.surface, borderRadius: 12,
    borderWidth: 1, borderColor: RD.borderStrong,
    marginTop: 4, maxHeight: 240, zIndex: 9999,
    shadowColor: '#000', shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.12, shadowRadius: 16, elevation: 12,
  },
  row: {
    paddingHorizontal: 16, paddingVertical: 12,
    borderBottomWidth: 1, borderBottomColor: RD.border,
  },
  rowText: { fontSize: 14, color: RD.text, fontFamily: fonts.regular },
});
